/* eslint-disable complexity */
/* eslint-disable max-statements */
import { BREAKPOINT_SPACING } from './constants';
import { createClosingVectors } from './create-closing-vectors';
import { nearestSixteenth, Vertex2 } from '@rta/rta-blueprint-library';
import { shiftVectorX } from './shift-vector-x';
import { splitVectorAtX, vectorCrossesX } from './split-vector-at-x';
import { Vector } from '@r-core/types';
export type SplitVectorBreakpointsResult = { vectors: Array<Vector<Vertex2>>, breakpoints: Array<number> };

type YRange = { minY: number; maxY: number };

type ProcessBreakpointResult = {
	newVectors: Array<Vector<Vertex2>>;
	leftYPositions: Array<number>;
	rightYPositions: Array<number>;
	leftExcludeRanges: Array<YRange>;
	rightExcludeRanges: Array<YRange>;
	wasModified: boolean;
};

// Check if a vertical is the LEFT edge of an appliance (horizontals extend RIGHT from it)
const isLeftEdgeOfAppliance = (vertical: Vector<Vertex2>, allVectors: Array<Vector<Vertex2>>, roundedSplitX: number, tolerance: number): boolean => {
	const verticalMinY = nearestSixteenth(Math.min(vertical.from.y, vertical.to.y));
	const verticalMaxY = nearestSixteenth(Math.max(vertical.from.y, vertical.to.y));
	const yTolerance = 0.5;

	for (const v of allVectors) {
		const vFromX = nearestSixteenth(v.from.x);
		const vToX = nearestSixteenth(v.to.x);
		const vFromY = nearestSixteenth(v.from.y);
		const vToY = nearestSixteenth(v.to.y);

		// Skip if not a horizontal vector
		if (Math.abs(vFromY - vToY) > 0.0625) continue;

		const horizontalY = vFromY;

		// Check if this horizontal connects to the vertical's endpoints
		const connectsToVertical = Math.abs(horizontalY - verticalMinY) <= yTolerance ||
								   Math.abs(horizontalY - verticalMaxY) <= yTolerance;
		if (!connectsToVertical) continue;

		// Check if one end is near splitX and the other extends RIGHT
		const fromNearSplitX = Math.abs(vFromX - roundedSplitX) <= tolerance;
		const toNearSplitX = Math.abs(vToX - roundedSplitX) <= tolerance;

		if (fromNearSplitX && vToX > roundedSplitX + tolerance) {
			return true; // Horizontal starts near splitX and goes right
		}
		if (toNearSplitX && vFromX > roundedSplitX + tolerance) {
			return true; // Horizontal ends near splitX, coming from right
		}
	}

	return false;
};

// Check if a vertical is the RIGHT edge of an appliance (horizontals extend LEFT from it)
const isRightEdgeOfAppliance = (vertical: Vector<Vertex2>, allVectors: Array<Vector<Vertex2>>, roundedSplitX: number, tolerance: number): boolean => {
	const verticalMinY = nearestSixteenth(Math.min(vertical.from.y, vertical.to.y));
	const verticalMaxY = nearestSixteenth(Math.max(vertical.from.y, vertical.to.y));
	const yTolerance = 0.5;

	for (const v of allVectors) {
		const vFromX = nearestSixteenth(v.from.x);
		const vToX = nearestSixteenth(v.to.x);
		const vFromY = nearestSixteenth(v.from.y);
		const vToY = nearestSixteenth(v.to.y);

		// Skip if not a horizontal vector
		if (Math.abs(vFromY - vToY) > 0.0625) continue;

		const horizontalY = vFromY;

		// Check if this horizontal connects to the vertical's endpoints
		const connectsToVertical = Math.abs(horizontalY - verticalMinY) <= yTolerance ||
								   Math.abs(horizontalY - verticalMaxY) <= yTolerance;
		if (!connectsToVertical) continue;

		// Check if one end is near splitX and the other extends LEFT
		// Mirror the logic from isLeftEdgeOfAppliance but for the opposite direction
		const fromNearSplitX = Math.abs(vFromX - roundedSplitX) <= tolerance;
		const toNearSplitX = Math.abs(vToX - roundedSplitX) <= tolerance;

		// Horizontal starts near splitX and goes left (to.x is significantly left of splitX)
		if (fromNearSplitX && !toNearSplitX && vToX < roundedSplitX) {
			return true;
		}
		// Horizontal ends near splitX, coming from the left (from.x is significantly left of splitX)
		if (toNearSplitX && !fromNearSplitX && vFromX < roundedSplitX) {
			return true;
		}
	}

	return false;
};

const processBreakpoint = (vectors: Array<Vector<Vertex2>>, splitX: number): ProcessBreakpointResult => {
	const roundedSplitX = nearestSixteenth(splitX);
	const newVectors: Array<Vector<Vertex2>> = [];
	const leftYPositions: Array<number> = [];
	const rightYPositions: Array<number> = [];
	const leftExcludeRanges: Array<YRange> = [];
	const rightExcludeRanges: Array<YRange> = [];
	let wasModified = false;

	const tolerance = 0.5; // 1/2" tolerance for position matching

	// First pass: find vertical vectors near splitX and determine which side they belong to
	// Use connectivity to horizontals to determine if it's a left or right edge of an appliance
	// Store the results in a Set for lookup in the second pass
	const leftEdgeVectors = new Set<Vector<Vertex2>>();
	const rightEdgeVectors = new Set<Vector<Vertex2>>();

	for (const vector of vectors) {
		const minX = nearestSixteenth(Math.min(vector.from.x, vector.to.x));
		const maxX = nearestSixteenth(Math.max(vector.from.x, vector.to.x));

		// Check if this is a vertical vector (same X for both endpoints)
		const isVertical = Math.abs(minX - maxX) < 0.0625; // Less than 1/16" difference = vertical
		const isNearSplitX = Math.abs(minX - roundedSplitX) <= tolerance;

		if (isVertical && isNearSplitX) {
			const minY = nearestSixteenth(Math.min(vector.from.y, vector.to.y));
			const maxY = nearestSixteenth(Math.max(vector.from.y, vector.to.y));

			const isLeftEdge = isLeftEdgeOfAppliance(vector, vectors, roundedSplitX, tolerance);
			const isRightEdge = isRightEdgeOfAppliance(vector, vectors, roundedSplitX, tolerance);

			if (isLeftEdge && !isRightEdge) {
				// This is a LEFT edge = appliance is on the RIGHT
				// Exclude from RIGHT closing vector
				leftEdgeVectors.add(vector);
				rightExcludeRanges.push({ minY, maxY });
				rightYPositions.push(minY, maxY);
			} else if (isRightEdge && !isLeftEdge) {
				// This is a RIGHT edge = appliance is on the LEFT
				// Exclude from LEFT closing vector
				rightEdgeVectors.add(vector);
				leftExcludeRanges.push({ minY, maxY });
				leftYPositions.push(minY, maxY);
			}
			// If both or neither, don't add to exclude ranges (might be panel edge or ambiguous)
		}
	}

	for (const vector of vectors) {
		const fromX = nearestSixteenth(vector.from.x);
		const toX = nearestSixteenth(vector.to.x);
		const minX = nearestSixteenth(Math.min(vector.from.x, vector.to.x));
		const maxX = nearestSixteenth(Math.max(vector.from.x, vector.to.x));

		// Check if vector crosses the splitX line
		if (vectorCrossesX(vector, splitX)) {
			const { left, right, yAtSplit } = splitVectorAtX(vector, splitX);
			// Vectors that cross contribute to both left and right closing
			leftYPositions.push(yAtSplit);
			rightYPositions.push(yAtSplit);
			newVectors.push(left, right);
			wasModified = true;
		} else {
			const gapRightEdge = nearestSixteenth(roundedSplitX + BREAKPOINT_SPACING);

			// Look up if this vector was identified as a left or right edge in the first pass
			const isLeftEdge = leftEdgeVectors.has(vector);
			const isRightEdge = rightEdgeVectors.has(vector);

			// Horizontal vectors that start at splitX and go right (with tolerance)
			const startsAtSplitXGoesRight = Math.abs(fromX - roundedSplitX) <= tolerance && toX > roundedSplitX + tolerance;
			// Horizontal vectors that end at splitX coming from the right (with tolerance)
			const endsAtSplitXFromRight = Math.abs(toX - roundedSplitX) <= tolerance && fromX > roundedSplitX + tolerance;
			// Horizontal vectors that end at splitX coming from the left (with tolerance)
			const endsAtSplitXFromLeft = Math.abs(toX - roundedSplitX) <= tolerance && fromX < roundedSplitX - tolerance;
			// Horizontal vectors that start at splitX and go left (with tolerance)
			const startsAtSplitXGoesLeft = Math.abs(fromX - roundedSplitX) <= tolerance && toX < roundedSplitX - tolerance;

			const isHorizontalExtendingRight = startsAtSplitXGoesRight || endsAtSplitXFromRight;
			const isHorizontalEndingAtSplitX = endsAtSplitXFromLeft || startsAtSplitXGoesLeft;

			// If vertical is a RIGHT edge of an appliance to the left - keep it as-is
			if (isRightEdge) {
				newVectors.push(vector);
				// Exclude ranges already added in first pass
			} else if (isLeftEdge) {
				// Left edge of appliance to the right - shift it
				newVectors.push(shiftVectorX(vector));
				wasModified = true;
				// Exclude ranges already added in first pass
			} else {
				// Record Y positions for closing vectors based on which side the vector belongs to
				if (isHorizontalEndingAtSplitX) {
					// Vector ends at splitX - contributes to left closing only
					const yAtSplitX = fromX === roundedSplitX ? vector.from.y : vector.to.y;
					leftYPositions.push(nearestSixteenth(yAtSplitX));
				}
				if (isHorizontalExtendingRight) {
					// Vector starts at splitX going right - contributes to right closing only
					const yAtSplitX = fromX === roundedSplitX ? vector.from.y : vector.to.y;
					rightYPositions.push(nearestSixteenth(yAtSplitX));
				}

				// Vectors past the gap need shifting
				// Vectors in the gap zone need shifting
				// Horizontal vectors touching splitX but extending right need shifting
				const shouldShift =
					minX > gapRightEdge ||
					(minX > roundedSplitX && minX < gapRightEdge) ||
					isHorizontalExtendingRight;

				if (shouldShift) {
					newVectors.push(shiftVectorX(vector));
					wasModified = true;
				} else {
					newVectors.push(vector);
				}
			}
		}
	}

	return { newVectors, leftYPositions, rightYPositions, leftExcludeRanges, rightExcludeRanges, wasModified };
};

export const splitVectorBreakpoints = (
	vectors: Array<Vector<Vertex2>>,
	xOffset: number,
	breakpoints: Array<number>
): SplitVectorBreakpointsResult => {
	const breakpointList: Array<number> = [];
	if (breakpoints.length === 0) return { vectors, breakpoints: breakpointList };

	// Sort breakpoints by center position (ascending)
	const sortedBreakpoints = [...breakpoints].sort((a, b) => a - b);

	let result = [...vectors];
	let accumulatedOffset = 0;

	for (let i = 0; i < sortedBreakpoints.length; i++) {
		const breakpoint = sortedBreakpoints[i];
		const splitX = breakpoint + xOffset + accumulatedOffset;
		breakpointList.push(splitX);

		const { newVectors, leftYPositions, rightYPositions, leftExcludeRanges, rightExcludeRanges, wasModified } = processBreakpoint(result, splitX);
		const closingVectors = createClosingVectors(splitX, leftYPositions, rightYPositions, leftExcludeRanges, rightExcludeRanges, result[0]);
		result = [...newVectors, ...closingVectors];

		// Only increase accumulated offset if vectors were actually split or shifted
		if (wasModified) {
			accumulatedOffset += BREAKPOINT_SPACING;
		}
	}

	return {
		vectors: result,
		breakpoints: breakpointList
	};
};
